import { useMemo } from "react";
import { ShoppingCart, AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@tellescope/utils";
import { Button } from "@tellescope/design-system";
import { useCartStore } from "../cartStore";
import { findVariant } from "../shopVariants";
import { groupSubscriptionTotals, formatCadenceTotal } from "../groupSubscriptionTotals";
import { CartItemRow } from "../molecules/CartItemRow";
import { RemovedItemsNotice } from "../molecules/RemovedItemsNotice";
import type { ShopProduct, ShopPriceVariant } from "../types";

interface CartPanelProps {
  products: ShopProduct[];
  onCheckout: () => void;
  onShop: () => void;
}

export const CartPanel = ({ products, onCheckout, onShop }: CartPanelProps) => {
  const { t } = useTranslation("shop");
  const items = useCartStore((state) => state.items);
  const pruneInvalidItems = useCartStore((state) => state.pruneInvalidItems);
  const handleDismissRemovedItemsNotice = () => pruneInvalidItems(products);

  const productsById = useMemo(() => new Map(products.map((p) => [p.stripeProductId, p])), [products]);

  const { cartRows, cadenceTotals, oneTimeTotal, currency } = useMemo(() => {
    const rows: { item: (typeof items)[number]; product: ShopProduct }[] = [];
    const subscriptionRows: { variant: ShopPriceVariant; quantity: number }[] = [];
    let oneTime = 0;
    let cur = "usd";
    for (const item of items) {
      const product = productsById.get(item.stripeProductId);
      if (!product) continue;
      const variant = findVariant(product, item.stripePriceId);
      if (!variant) continue;
      rows.push({ item, product });
      cur = variant.currency;
      if (variant.isSubscription) {
        subscriptionRows.push({ variant, quantity: item.quantity });
      } else {
        oneTime += variant.amount * item.quantity;
      }
    }
    // Grouped by distinct billing cadence (e.g. monthly vs. quarterly) rather than summed into one
    // "monthly" bucket — different cadences are different units and can't be added together into a
    // single "/mo" figure. Stripe Checkout itself can only charge one subscription billing interval
    // per session ("Checkout does not support multiple prices with different billing intervals"), so
    // more than one entry here means this cart cannot check out as-is — see hasMixedCadences below.
    return {
      cartRows: rows,
      cadenceTotals: groupSubscriptionTotals(t, subscriptionRows),
      oneTimeTotal: oneTime,
      currency: cur,
    };
  }, [t, items, productsById]);
  // A stale item (its product removed from the Shop block's config after being added to the cart)
  // stays in the store — and this notice keeps showing — until the enduser dismisses it (see
  // handleDismissRemovedItemsNotice above). cartRows already excludes exactly these items, so a
  // shorter cartRows than items directly means one or more were filtered out — no separate scan
  // needed. Derived directly during render: a plain boolean, not an effect.
  const hasRemovedItems = cartRows.length < items.length;
  const hasMixedCadences = cadenceTotals.length > 1;
  // Mirrors CartItemRow's own condition for rendering BillingToggle — only mention "adjust your
  // billing schedule" in the warning below when at least one cart item actually has that control
  // available. A single-price-variant product has no toggle to adjust; removing it from the cart
  // is that item's only real path to resolving a mixed-cadence cart.
  const hasAdjustableItem = cartRows.some(({ product }) => product.variants.length > 1);

  if (cartRows.length === 0) {
    return (
      <div className="p-8 max-w-xl mx-auto w-full">
        <h2 className="text-2xl font-bold text-foreground mb-6">{t("Your Cart")}</h2>
        {hasRemovedItems && <RemovedItemsNotice onDismiss={handleDismissRemovedItemsNotice} />}
        <div className="flex flex-col items-center justify-center py-20 gap-5 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <ShoppingCart className="w-8 h-8 text-muted-foreground" />
          </div>
          <div>
            <p className="text-base font-semibold text-foreground mb-1">{t("Empty cart")}</p>
            <p className="text-sm text-muted-foreground">{t("Add medications from the shop to get started")}</p>
          </div>
          <Button variant="outline" onClick={onShop}>
            {t("Browse medications")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-xl mx-auto w-full">
      <h2 className="text-2xl font-bold text-foreground mb-6">{t("Your Cart")}</h2>

      {hasRemovedItems && <RemovedItemsNotice onDismiss={handleDismissRemovedItemsNotice} />}

      <div className="bg-card border border-border rounded-2xl px-4 mb-6">
        {cartRows.map(({ item, product }) => (
          <CartItemRow key={item.stripeProductId} item={item} product={product} />
        ))}
      </div>

      <div className="bg-card border border-border rounded-2xl p-4 mb-6 space-y-2">
        {cadenceTotals.map((total) => (
          <div key={total.key} className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("Subscription subtotal")}</span>
            <span className="font-medium">{formatCadenceTotal(total)}</span>
          </div>
        ))}
        {oneTimeTotal > 0 && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t("One-time subtotal")}</span>
            <span className="font-medium">{formatCurrency(oneTimeTotal, currency, { fromSmallestUnit: true })}</span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{t("Shipping")}</span>
          <span className="font-medium">{t("Calculated at checkout")}</span>
        </div>
      </div>

      {hasMixedCadences && (
        <div
          data-test="mixed-cadence-warning"
          className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            {hasAdjustableItem
              ? t(
                  "Your cart has items on different billing schedules. Adjust an item's billing schedule above so they match, or complete these as separate orders."
                )
              : t(
                  "Your cart has items on different billing schedules. Complete these as separate orders — remove one to continue."
                )}
          </span>
        </div>
      )}

      <Button
        data-test="cart-checkout"
        className="w-full sticky bottom-6 shadow-lg shadow-foreground/10"
        size="lg"
        disabled={hasMixedCadences}
        onClick={onCheckout}
      >
        {t("Checkout")}
      </Button>
    </div>
  );
};
